// S3 — Hero-power targeting overlay.
//
// Rendered above the tavern when `heroPowerState === 'targeting'`. Dims
// the rest of the UI and pulses the valid targets (board slots or shop
// cards). Clicking a target calls `selectHeroPowerTarget(idx)`. ESC and
// clicking the dim backdrop both call `cancelHeroPower()`.
//
// The component does NOT mount or render the targets itself — those are
// already painted by TavernPage. Instead we render a full-screen
// backdrop with a hole punched over the relevant zone via a sodium
// pulse ring + click-capture region. To keep the implementation simple
// we use a portal-less absolute-positioned overlay that the parent
// stacks above its existing zones; the targets themselves are still
// driven by TavernPage's normal grid (we just ride sodium ring styles
// on top via the `pulseTargets` render-prop pattern).
//
// API:
//   <HeroPowerTargetingOverlay
//     targetType="unit"            // 'unit' | 'shop'
//     count={7}                    // number of slots / shop items
//     isValidTarget={(i) => ...}   // which indices are pulsing-clickable
//     onSelect={(i) => ...}        // selects target i
//     onCancel={() => ...}         // ESC / backdrop tap
//     anchorRef={boardSectionRef}  // optional — we don't currently use
//                                  // it; the backdrop sits over the full
//                                  // viewport and only the highlight
//                                  // ring of each target is positioned
//                                  // by TavernPage itself.
//   />
//
// Visual decisions:
//   * The overlay is a single absolutely-positioned fixed div with a
//     dark dim. It renders a "Pick a target" banner at the top with a
//     Cancel chip.
//   * Targets pulse via TavernPage adding a `data-hp-target` flag to
//     each candidate; we don't reach into their DOM. TavernPage maps
//     that flag to a sodium ring + pulse animation in its own grid.
//   * This keeps the component-shape clean while making sure the
//     existing dnd-kit listeners on board cards are NOT triggered — the
//     parent suppresses drag wiring while `heroPowerState !== 'idle'`.
//
// ESC handling: registered on the window so closing works from anywhere.

import { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface HeroPowerTargetingOverlayProps {
  /** Visible when true. Parent should pass `state === 'targeting'`. */
  open: boolean;
  /** What the player is picking. Drives the banner copy only. */
  targetType: 'unit' | 'shop' | null;
  /** Cancel handler — also fires on ESC + backdrop tap. */
  onCancel: () => void;
  /** Optional hero-power name for the banner copy. */
  powerName?: string;
}

export function HeroPowerTargetingOverlay({
  open,
  targetType,
  onCancel,
  powerName,
}: HeroPowerTargetingOverlayProps) {
  const cancelButtonRef = useRef<HTMLButtonElement | null>(null);

  // ESC closes.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  // Move keyboard focus to the Cancel button when the overlay opens so
  // Tab cycles within the modal-ish surface instead of bleeding back to
  // the (dimmed) tavern controls underneath. ESC still closes; clicking
  // the dim backdrop still cancels; mouse/touch reaching a pulsing
  // target still works because the targets are NOT covered by the
  // backdrop's pointer events.
  useEffect(() => {
    if (!open) return;
    const id = window.setTimeout(() => {
      cancelButtonRef.current?.focus();
    }, 0);
    return () => window.clearTimeout(id);
  }, [open]);

  const banner =
    targetType === 'shop'
      ? 'Pick a shop card'
      : targetType === 'unit'
        ? 'Pick a unit on your board'
        : 'Pick a target';

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Dim backdrop — clicking cancels. Below the banner z-wise
              so the banner stays interactive. The targets themselves
              (rendered by TavernPage) sit in their normal flow but
              receive a sodium pulse ring via Tailwind classes
              conditioned on `heroPowerState === 'targeting'`. */}
          <motion.div
            key="hp-overlay-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-30 bg-asphalt/55 backdrop-blur-[1px]"
            onClick={onCancel}
            role="presentation"
            aria-hidden
          />

          {/* Banner — fixed at the top, above the dim. Has its own
              cancel chip; ESC also works. */}
          <motion.div
            key="hp-overlay-banner"
            initial={{ y: -16, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -12, opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="pt-safe fixed inset-x-0 top-0 z-40 flex items-center justify-center px-md py-md md:px-lg md:py-lg"
            role="dialog"
            aria-modal="true"
            aria-label="hero power targeting"
          >
            <div
              className="flex w-full max-w-md items-center justify-between gap-sm rounded-md border bg-stucco2 px-md py-sm shadow-sodium md:max-w-lg md:px-lg md:py-md"
              style={{ borderColor: 'rgba(255,138,61,0.45)' }}
            >
              <div className="flex flex-col">
                <span className="font-data text-[10px] uppercase tracking-[0.3em] text-sodium md:text-[11px]">
                  hero power
                </span>
                <span className="font-data text-[13px] uppercase tracking-[0.2em] text-bone md:text-[15px]">
                  {powerName ? `${powerName} · ${banner}` : banner}
                </span>
              </div>
              <button
                ref={cancelButtonRef}
                onClick={onCancel}
                className="min-h-[40px] rounded-sm bg-stucco3 px-md font-data text-[11px] uppercase tracking-[0.2em] text-bone transition-colors hover:bg-aerosolRed focus:outline-none focus:ring-2 focus:ring-sodium md:text-[12px]"
                aria-label="cancel hero power"
              >
                Cancel
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
